import {
  GraduationCap,
  Users,
  BookOpen,
  UserCheck,
  Calendar,
  DollarSign,
  BarChart3,
  Bell,
  Shield,
  Cloud,
  Zap,
  CheckCircle2,
  TrendingUp,
  Clock,
  FileText,
  Settings,
  MessageSquare,
  Database,
  Lock,
  Smartphone,
  Globe,
  Award,
  Target,
  Heart,
} from "lucide-react";

// Hero Section Data
export const heroSection = {
  badge: "All-in-One School Management Platform",
  title: "EduAdminHub",
  highlightedText: "Transform Your School Administration",
  description:
    "A comprehensive, cloud-based platform designed to streamline every aspect of school management — from admissions and attendance tracking to fee management and reporting. Empower your institution with cutting-edge technology that simplifies operations and enhances productivity.",
  cta: {
    primary: "Schedule Demo",
    secondary: "Explore Features",
  },
  stats: [
    { value: "500+", label: "Schools Trust Us" },
    { value: "2M+", label: "Students Managed" },
    { value: "99.9%", label: "Uptime Guarantee" },
    { value: "24/7", label: "Support Available" },
  ],
};

// Challenges Section
export const challenges = {
  title: "The Challenges",
  highlightedText: "Schools Face Today",
  description:
    "Modern school administration is filled with complex tasks, repetitive manual work, and fragmented systems that leave educators and staff overwhelmed. Instead of focusing on delivering quality education, schools often get bogged down by operational inefficiencies.",
  cards: [
    {
      icon: FileText,
      title: "Manual Data Management",
      description:
        "Schools relying on paper records, registers, and spreadsheets face frequent errors, misplaced data, and time-consuming updates. Staff spend hours entering and reconciling information manually, increasing the risk of mistakes.",
      gradient: "from-red-500/10 to-orange-500/10",
      iconColor: "text-red-500",
    },
    {
      icon: Database,
      title: "No Central System",
      description:
        "Without a unified platform, critical information like student records, attendance logs, and fee details are scattered across multiple systems or files. This forces administrators to switch between tools and reconcile data manually.",
      gradient: "from-blue-500/10 to-cyan-500/10",
      iconColor: "text-blue-500",
    },
    {
      icon: Settings,
      title: "Inefficient Processes",
      description:
        "Outdated and disconnected workflows slow down communication between staff, teachers, and management. Important updates and actions are often delayed because of unclear processes, causing frustration and reduced engagement.",
      gradient: "from-purple-500/10 to-pink-500/10",
      iconColor: "text-purple-500",
    },
  ],
};

// Solution Features
export const solutionFeatures = {
  title: "Why EduAdminHub is the",
  highlightedText: "Perfect Solution",
  description:
    "EduAdminHub provides schools with a powerful, user-friendly platform that simplifies and streamlines all administrative tasks. It connects administrators and staff through a single system, helping schools run more efficiently, reduce errors, and focus on what truly matters — delivering quality education.",
  features: [
    {
      icon: Cloud,
      title: "Cloud-Based, Accessible Anytime",
      description:
        "EduAdminHub is a secure, cloud-powered platform that lets your team manage school operations from anywhere — whether on campus or remote. No complex installations or maintenance required. All data is updated in real-time and accessible on any device.",
      gradient: "from-blue-500/20 to-cyan-500/20",
      iconColor: "text-blue-500",
    },
    {
      icon: Zap,
      title: "All-in-One Centralized System",
      description:
        "EduAdminHub brings together all critical school operations — from admissions and attendance to fee management and reporting — into a single, easy-to-use platform. No more juggling multiple tools or spreadsheets.",
      gradient: "from-yellow-500/20 to-orange-500/20",
      iconColor: "text-yellow-500",
    },
    {
      icon: Shield,
      title: "Secure, Reliable & Easy to Use",
      description:
        "Built with modern security standards, EduAdminHub ensures your data is safe with encryption and role-based access controls. The platform is designed with simplicity in mind, so your staff can get started with minimal training.",
      gradient: "from-green-500/20 to-emerald-500/20",
      iconColor: "text-green-500",
    },
  ],
};

// Module Features
export const moduleFeatures = {
  title: "Explore Our Powerful",
  highlightedText: "Module Features",
  description:
    "Comprehensive modules designed to handle every aspect of school management efficiently and effectively.",
  modules: [
    {
      id: "dashboard",
      title: "Dashboard",
      icon: BarChart3,
      description:
        "Your all-in-one control center, tailored for every user role. Get quick access to key information and actions that matter to you.",
      color: "text-cyan-500",
      bgGradient: "from-cyan-500/10 to-blue-500/10",
      features: [
        "Admin: View institution metrics, manage modules, and track performance",
        "Teacher: Manage classes, assignments, attendance, and student progress",
        "Student/Parent: Check schedules, grades, homework, and updates",
      ],
      details:
        "Our dynamic dashboards provide personalized views for admins, teachers, students, and parents. Whether it's monitoring performance, staying updated on tasks, or tracking progress — everything is just a click away.",
    },
    {
      id: "smart-management",
      title: "Smart Management",
      icon: Smartphone,
      description:
        "Manage your institution easily with our powerful website and mobile app — built for convenience anytime, anywhere.",
      color: "text-purple-500",
      bgGradient: "from-purple-500/10 to-pink-500/10",
      features: [
        "Accessible on web and app — manage on the go or from your desk",
        "User-friendly interface — simple, clean design for smooth navigation",
        "Custom layouts — choose what works best for your team",
      ],
      details:
        "EduAdminHub gives you the freedom to manage your school or college from both our website and mobile app. With flexible layouts like mini view, RTL support, and dark mode, you can work comfortably no matter where you are.",
    },
    {
      id: "people",
      title: "People Management",
      icon: Users,
      description:
        "Easily manage everyone connected to your institution in one place.",
      color: "text-green-500",
      bgGradient: "from-green-500/10 to-emerald-500/10",
      features: [
        "Students: Store profiles with personal details, academics, attendance, and more",
        "Parents: Keep contact info, payment history, and linked student records",
        "Teachers: Manage subjects, schedules, and performance data",
      ],
      details:
        "EduAdminHub helps you handle students, parents, and teachers through a single platform. From registrations to records, everything stays organized and easy to access — so you can focus on what matters most: education.",
    },
    {
      id: "academics",
      title: "Academics",
      icon: BookOpen,
      description:
        "Streamline and automate all key academic operations with ease.",
      color: "text-red-500",
      bgGradient: "from-red-500/10 to-pink-500/10",
      features: [
        "Manage classes, sections, and subjects effortlessly",
        "Create and update timetables, routines, and syllabus",
        "Simplify homework, exams, and result management",
      ],
      details:
        "EduAdminHub's academic module helps schools and colleges organize everything from class structures and room allocations to exam schedules and grading — all in one place. It ensures smooth operations, saves time, and keeps your academic activities well-coordinated.",
    },
    {
      id: "hrm",
      title: "HR Management",
      icon: UserCheck,
      description:
        "Simplify and streamline staff management for your institution.",
      color: "text-blue-500",
      bgGradient: "from-blue-500/10 to-indigo-500/10",
      features: [
        "Manage staff records — Store and access personal details, qualifications, and roles",
        "Track attendance & leaves — Monitor attendance, handle leave requests, and view balances",
        "Automate payroll — Calculate salaries with deductions, overtime, and leave adjustments",
      ],
      details:
        "Our HR Management module makes it easy to handle staff records, organize departments, define designations, manage attendance and leaves, publish holiday calendars, and automate payroll — all in one platform for smooth and efficient operations.",
    },
    {
      id: "notifications",
      title: "Notifications & Communication",
      icon: Bell,
      description:
        "Keep everyone updated with important news and alerts.",
      color: "text-orange-500",
      bgGradient: "from-orange-500/10 to-amber-500/10",
      features: [
        "Notice Board: Share exam dates, events, holidays, and more",
        "Events: Plan, schedule, and track all school activities",
        "Email Alerts: Send updates on homework, timetables, leave requests, and more",
      ],
      details:
        "Stay connected with 25+ automated email notifications. EduAdminHub keeps students, staff, and parents informed — from registration updates and homework alerts to timetables, library records, leave requests, and more.",
    },
    {
      id: "user-management",
      title: "User Management",
      icon: Settings,
      description:
        "Easily manage user accounts, access, and security in one place.",
      color: "text-indigo-500",
      bgGradient: "from-indigo-500/10 to-purple-500/10",
      features: [
        "Manage Accounts: Create, update, and view user profiles",
        "Roles & Permissions: Set role-based access for better control",
        "Secure Login: Enable safe sign-in with multi-factor authentication",
      ],
      details:
        "EduAdminHub's user management helps you control who can access what, keeping your platform secure and organized. From handling account requests to defining permissions, everything is simple, safe, and efficient.",
    },
    {
      id: "reports",
      title: "Reports & Analytics",
      icon: TrendingUp,
      description:
        "Get clear insights to support smarter decisions.",
      color: "text-teal-500",
      bgGradient: "from-teal-500/10 to-cyan-500/10",
      features: [
        "Attendance & Leave Reports: Track attendance patterns and leave records",
        "Academic Reports: Monitor class performance, student progress, and subject-wise grades",
        "Fee & Payment Reports: Keep an eye on collected fees, pending dues, and late payments",
      ],
      details:
        "Our reporting tools help schools and colleges stay informed at every level. From academic progress to financial tracking, access detailed reports that make managing your institution easier and more efficient.",
    },
    {
      id: "support",
      title: "Support & Help",
      icon: MessageSquare,
      description:
        "Quick help when you need it, with reliable communication and solutions.",
      color: "text-pink-500",
      bgGradient: "from-pink-500/10 to-rose-500/10",
      features: [
        "Contact Messages – Handle user questions and feedback easily",
        "Tickets – Track and resolve issues fast",
        "Ongoing Help – Continuous assistance for smooth operations",
      ],
      details:
        "Our support system ensures your queries are heard and resolved without delay. From managing messages to solving technical issues, we're here to keep your experience hassle-free.",
    },
  ],
};

// Roles Section
export const roles = {
  title: "Helping Everyone",
  highlightedText: "Perform Better",
  description:
    "EduAdminHub is designed to empower every stakeholder in the education ecosystem with the tools they need to succeed.",
  roles: [
    {
      icon: GraduationCap,
      title: "Schools",
      description:
        "Empower your institution with EduAdminHub's all-in-one school management solution. Streamline operations, enhance efficiency, and reduce manual workload—so you can focus on delivering quality education.",
      gradient: "from-blue-500/20 to-cyan-500/20",
      iconColor: "text-blue-500",
    },
    {
      icon: BookOpen,
      title: "Teachers",
      description:
        "Transform teaching with EduAdminHub's advanced digital tools and curriculum resources. Simplify your daily tasks and foster a dynamic, engaging classroom environment for every learner.",
      gradient: "from-green-500/20 to-emerald-500/20",
      iconColor: "text-green-500",
    },
    {
      icon: Users,
      title: "Students",
      description:
        "Stay ahead in your academic journey with EduAdminHub. Access learning materials, recorded lectures, and endless practice questions—all in one platform built for continuous growth.",
      gradient: "from-purple-500/20 to-pink-500/20",
      iconColor: "text-purple-500",
    },
    {
      icon: Heart,
      title: "Parents",
      description:
        "Stay informed and involved in your child's education. With EduAdminHub, you can track academic performance, receive real-time updates, and ensure full visibility into school activities.",
      gradient: "from-orange-500/20 to-red-500/20",
      iconColor: "text-orange-500",
    },
  ],
};

// Pricing Plans
export const pricingPlans = {
  title: "Our",
  highlightedText: "Pricing Plans",
  description:
    "Flexible pricing designed to match your institution's size, structure, and requirements. Get exactly what you need — nothing more, nothing less.",
  plans: [
    {
      name: "Starter",
      price: "Free",
      period: "forever",
      description: "Perfect for small schools getting started",
      features: [
        "Up to 100 students",
        "Basic attendance tracking",
        "Fee management",
        "Email support",
        "Mobile app access",
      ],
      cta: "Get Started",
      popular: false,
      gradient: "from-gray-500/10 to-gray-600/10",
    },
    {
      name: "Professional",
      price: "₹29,999",
      period: "per year",
      description: "Ideal for growing institutions",
      features: [
        "Up to 500 students",
        "All Starter features",
        "Advanced reporting & analytics",
        "HR management module",
        "Priority support",
        "Custom integrations",
        "Training sessions",
      ],
      cta: "Start Free Trial",
      popular: true,
      gradient: "from-primary/20 to-primary/10",
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "pricing",
      description: "For large institutions and multi-campus",
      features: [
        "Unlimited students",
        "All Professional features",
        "Multi-campus management",
        "Dedicated account manager",
        "24/7 priority support",
        "Custom development",
        "API access",
        "White-label options",
      ],
      cta: "Contact Sales",
      popular: false,
      gradient: "from-purple-500/20 to-pink-500/20",
    },
  ],
};

// Registration Steps
export const registrationSteps = {
  title: "Process to",
  highlightedText: "Register & Start",
  description:
    "Get started with EduAdminHub in just a few simple steps. Our streamlined onboarding process ensures you're up and running quickly.",
  steps: [
    {
      number: "01",
      title: "Sign Up for EduAdminHub",
      description:
        "Create your account by providing basic information about your institution. Our team will verify your details and activate your account within 24 hours.",
      icon: UserCheck,
    },
    {
      number: "02",
      title: "Set Up Your School Profile",
      description:
        "Configure your school details, including name, address, contact information, academic year, and other essential settings. Our guided setup wizard makes this process simple.",
      icon: Settings,
    },
    {
      number: "03",
      title: "Import or Add Student & Staff Data",
      description:
        "Easily import existing data from spreadsheets or other systems, or manually add students and staff members. Our bulk import feature saves you time and effort.",
      icon: Database,
    },
    {
      number: "04",
      title: "Start Managing Your School Operations",
      description:
        "Begin using all the powerful features of EduAdminHub. Our support team is available to help you get the most out of the platform with training and assistance.",
      icon: CheckCircle2,
    },
  ],
};

// FAQs
export const faqs = {
  title: "Frequently Asked",
  highlightedText: "Questions",
  description:
    "Find answers to common questions about EduAdminHub. If you have additional questions, feel free to contact our support team.",
  questions: [
    {
      question: "Is EduAdminHub suitable for schools and colleges of any size?",
      answer:
        "Yes! EduAdminHub is designed for both schools and colleges, whether you're a small institution or a large multi-campus organization. The platform is flexible, so you can select the features that best match your institution's size, structure, and specific needs. We offer different pricing tiers to accommodate institutions of all sizes.",
    },
    {
      question: "Do I need to install any software to use EduAdminHub?",
      answer:
        "No installation required! EduAdminHub is a cloud-based platform that runs entirely in your web browser. You can also access it through our mobile apps available for iOS and Android. All you need is an internet connection and a device with a modern web browser.",
    },
    {
      question: "Can we migrate our existing student and staff data into EduAdminHub?",
      answer:
        "Absolutely! Our team will assist you in securely importing your existing data, whether it's from spreadsheets, Excel files, or other school management systems. We provide data migration services and templates to ensure a smooth transition. Our support team will guide you through the entire process.",
    },
    {
      question: "How secure is our data on EduAdminHub?",
      answer:
        "Data security is our top priority. EduAdminHub uses industry-standard encryption (SSL/TLS) for data transmission, secure servers with regular backups, and role-based access controls to keep your school's information safe. We comply with data protection regulations and conduct regular security audits to ensure your data remains protected.",
    },
    {
      question: "Do you offer training or support for new users?",
      answer:
        "Yes! We provide comprehensive onboarding support, detailed user guides, video tutorials, and live training sessions to help your staff get started quickly. Our support team is always available via email, phone, or live chat to assist when you need help. We also offer dedicated training programs for larger institutions.",
    },
    {
      question: "Is there a free trial or demo available?",
      answer:
        "EduAdminHub offers personalized demos by request, where we walk you through the platform and its features based on your institution's needs. We also provide a free Starter plan for small schools. For larger institutions, we offer a 30-day free trial of our Professional plan so you can experience all features before committing.",
    },
    {
      question: "Can EduAdminHub integrate with other systems we're using?",
      answer:
        "Yes, EduAdminHub offers API access and can integrate with various third-party systems including accounting software, payment gateways, SMS providers, and more. Our Enterprise plan includes custom integration services. Contact our sales team to discuss your specific integration requirements.",
    },
    {
      question: "What kind of support do you provide?",
      answer:
        "We offer multiple support channels including email support, phone support, live chat, and a comprehensive knowledge base. Our Professional and Enterprise plans include priority support with faster response times. Enterprise customers also get a dedicated account manager and 24/7 support access.",
    },
  ],
};

// Benefits/Stats Section
export const benefits = {
  title: "Why Schools Choose",
  highlightedText: "EduAdminHub",
  stats: [
    {
      icon: TrendingUp,
      value: "85%",
      label: "Time Saved on Admin Tasks",
      description: "Automate repetitive tasks and focus on education",
      color: "text-green-500",
    },
    {
      icon: Award,
      value: "500+",
      label: "Schools Trust Us",
      description: "Join hundreds of satisfied institutions",
      color: "text-blue-500",
    },
    {
      icon: Target,
      value: "99.9%",
      label: "Uptime Guarantee",
      description: "Reliable service you can count on",
      color: "text-purple-500",
    },
    {
      icon: Clock,
      value: "24/7",
      label: "Support Available",
      description: "Get help whenever you need it",
      color: "text-orange-500",
    },
  ],
};
